/* Rhody Reviews api/v1 -- competitors domain. PURE EXTRACTION from lib/api.ts (gate-prep split); no behavior change. */
import { request } from './_http';

/* Competitors (GET/POST/DELETE /competitors). */
export interface Competitor {
  id: string;
  competitor_label: string;
  competitor_place_id: string;
  snapshot_date: string | null;
  total_reviews: number;
  avg_rating: number;
  new_in_period: number;
  [k: string]: unknown;
}
export interface CompetitorsResponse {
  client_id: string;
  count: number;
  competitors: Competitor[];
}
export interface CompetitorInput {
  competitor_label: string;
  competitor_place_id: string;
  total_reviews?: number;
  avg_rating?: number;
  new_in_period?: number;
}
/* Result of POST /competitors/refresh. refreshed = how many got a fresh Google
   snapshot; errors lists per-competitor fetch/store failures (last-known kept).
   degraded='places_unconfigured' means no Places key -> nothing refreshed, the
   list is unchanged (never fabricated). */
export interface CompetitorsRefreshResult {
  client_id: string;
  refreshed: number;
  degraded?: string;
  errors: { competitor_place_id: string; reason: string }[];
  competitors: Competitor[];
}

/* A single business match from the owner-facing "search by name" picker
   (GET /places/search). The owner sees name + address only; the place_id
   is carried behind the scenes and sent to POST /competitors on select.
   NEVER render place_id in the UI. */
export interface PlaceMatch {
  place_id: string;
  name: string;
  address: string;
}
export interface PlacesSearchResponse {
  results: PlaceMatch[];
}

/* ----------------------------------------------------------------------
   Competitor SCORECARD (GET /competitors/scorecard).

   Derived metrics over the snapshot history + a market leaderboard with the
   client's rank and gap-to-leader. HONESTY IS BAKED IN: every derived block
   carries a `basis`. When basis === 'not_enough_data' the numbers are null and
   the UI must say "not enough data yet" -- never invent a trend or a number.
   competitor_place_id is an OPAQUE correlation key; it is NEVER displayed.
   ---------------------------------------------------------------------- */
export type TrajectoryDirection = 'up' | 'down' | 'flat';
export type ScorecardBasis =
  | 'ok'
  | 'not_enough_data'
  | 'not_at_current_pace'
  | 'already_leading';

export interface ScorecardVelocity {
  reviews_per_month: number | null;
  basis: string; // 'ok' | 'not_enough_data'
  window_days: number;
}
export interface ScorecardTrajectory {
  direction: TrajectoryDirection | null;
  delta: number | null;
  basis: string; // 'ok' | 'not_enough_data'
}
export interface ScorecardRecency {
  days_since_snapshot: number | null;
  days_since_new_review: number | null;
  basis: string; // 'ok' | 'not_enough_data'
}
export interface ScorecardVulnerability {
  is_vulnerable: boolean;
  flags: string[]; // 'closed' | 'stagnant' | 'declining'
  reasons: string[]; // human-readable, e.g. "Permanently closed"
}
export interface CompetitorCard {
  /* OPAQUE key only -- never render this. */
  competitor_place_id: string;
  label: string;
  category: string | null;
  total_reviews: number;
  avg_rating: number | null;
  business_status: string | null; // OPERATIONAL | CLOSED_TEMPORARILY | CLOSED_PERMANENTLY | null
  website_uri: string | null;
  phone: string | null;
  price_level: number | string | null;
  opening_hours: unknown[] | null;
  velocity: ScorecardVelocity;
  trajectory: ScorecardTrajectory;
  recency: ScorecardRecency;
  vulnerability: ScorecardVulnerability;
  snapshot_count: number;
}
export interface LeaderboardRow {
  rank: number;
  is_client: boolean;
  label: string;
  total_reviews: number;
  avg_rating: number | null;
  reviews_per_month: number | null;
  /* OPAQUE key only -- never render this. */
  competitor_place_id: string;
}
export interface ScorecardEta {
  months: number | null;
  basis: string; // 'ok' | 'not_at_current_pace' | 'not_enough_data' | 'already_leading'
}
export interface ScorecardLeaderboard {
  rows: LeaderboardRow[];
  client_rank: number;
  field_size: number;
  leader: { label: string; total_reviews: number } | null;
  gap_to_leader: number;
  eta_to_leader: ScorecardEta;
}
export interface CompetitorScorecardResponse {
  client_id: string;
  competitors: CompetitorCard[];
  leaderboard: ScorecardLeaderboard;
}

export const competitorsApi = {
  /* ----- Competitors ----- */
  listCompetitors(signal?: AbortSignal): Promise<CompetitorsResponse> {
    return request<CompetitorsResponse>('/api/v1/competitors', { signal });
  },
  createCompetitor(
    input: CompetitorInput,
  ): Promise<{ client_id: string; competitor: Competitor }> {
    return request<{ client_id: string; competitor: Competitor }>('/api/v1/competitors', {
      method: 'POST',
      body: input,
    });
  },
  deleteCompetitor(placeId: string): Promise<{ client_id: string; deleted: string }> {
    return request<{ client_id: string; deleted: string }>(
      `/api/v1/competitors/${encodeURIComponent(placeId)}`,
      { method: 'DELETE' },
    );
  },
  /* Owner-facing business PICKER. Search Google by business name (+ optional
     city/location) and return a short pick-list of {name, address, place_id}.
     The UI shows name + address; on select it sends the matched place_id to
     createCompetitor behind the scenes. Bills per call -> the caller debounces.
     A 200 with results:[] is "no matches", NOT an error. */
  searchPlaces(q: string, near?: string, signal?: AbortSignal): Promise<PlacesSearchResponse> {
    return request<PlacesSearchResponse>('/api/v1/places/search', {
      query: { q, near: near || undefined },
      signal,
    });
  },
  /* Pull live rating + review counts from Google Places for every tracked
     competitor and store fresh snapshots. Costs a few cents per call. */
  refreshCompetitors(): Promise<CompetitorsRefreshResult> {
    return request<CompetitorsRefreshResult>('/api/v1/competitors/refresh', {
      method: 'POST',
      body: {},
    });
  },
  /* Competitor SCORECARD -- derived metrics + market leaderboard, read from
     stored snapshots only (no Google call here). Honest by construction:
     metrics with basis 'not_enough_data' come back null. */
  getCompetitorScorecard(signal?: AbortSignal): Promise<CompetitorScorecardResponse> {
    return request<CompetitorScorecardResponse>('/api/v1/competitors/scorecard', { signal });
  },
};
